import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/utils'
import { ParsleyMark } from './parsley-mark'

// The Parsley logo — the leaf mark centered in a rounded square (the app-icon form used in
// the header, favicons, and avatars). `tone` pairs a background with a legible mark color;
// `size` scales the square. The mark is sized as a fraction of the square (see MARK_CLASS)
// so it stays optically centered and proportional at every step. Use tone="clearspace" for
// the bare mark with no container — it then inherits the surrounding `currentColor`.
const logoVariants = cva('inline-flex flex-shrink-0 items-center justify-center', {
  variants: {
    tone: {
      green: 'bg-brand-green-500 text-white shadow-card',
      'dark-green': 'bg-brand-green-dark text-white shadow-card',
      white: 'bg-white text-brand-green shadow-card',
      clearspace: 'bg-transparent text-brand-green shadow-none'
    },
    // Fixed px (not rem) so the badge never scales with the app's root font-size clamp —
    // brand artwork should be a deliberate size, identical on every screen.
    size: {
      sm: 'size-[28px] rounded-[8px]',
      md: 'size-[36px] rounded-[10px]',
      lg: 'size-[48px] rounded-[12px]',
      xl: 'size-[64px] rounded-[16px]'
    }
  },
  defaultVariants: {
    tone: 'green',
    size: 'md'
  }
})

// Mark footprint inside the square. Width/height are set as explicit fractions (the mark's
// 696:621 aspect ratio, w ≈ h × 1.12) rather than relying on intrinsic SVG sizing, so it
// renders identically regardless of any global svg reset.
const MARK_CLASS = 'h-1/2 w-[56%]'

export interface ParsleyLogoProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof logoVariants> {}

export const ParsleyLogo = React.forwardRef<HTMLSpanElement, ParsleyLogoProps>(({ className, tone, size, ...props }, ref) => (
  <span ref={ref} className={cn(logoVariants({ tone, size }), className)} {...props}>
    <ParsleyMark filled className={MARK_CLASS} />
  </span>
))
ParsleyLogo.displayName = 'ParsleyLogo'

export { logoVariants }
